import { InteractionRequiredAuthError, type AccountInfo } from "@azure/msal-browser";
import { loginRequest, msalInitPromise, msalInstance } from "./msalInstance";

/**
 * Tijdelijke pech (netwerk, Microsoft nog bezig). Opnieuw proberen heeft zin.
 * Een verlopen sessie hoort hier niet bij: die vraagt een echte aanmelding.
 */
const TRANSIENT_ERROR_CODES = new Set([
  "interaction_in_progress",
  "monitor_window_timeout",
  "block_iframe_reload",
  "iframe_closed_prematurely",
  "timed_out",
  "post_request_failed",
  "get_request_failed",
  "no_network_connectivity",
]);

/** Voorkomt een lus: na een mislukte rondgang naar Microsoft niet meteen opnieuw. */
const INTERACTIVE_AT_KEY = "domaddress.msalInteractiveAt";
const INTERACTIVE_COOLDOWN_MS = 90_000;

export const AUTH_REQUIRED_EVENT = "app:auth-required";

export type IdTokenResult = {
  token: string | null;
  /** De browser gaat nu naar Microsoft; de pagina wordt zo verlaten. */
  redirecting: boolean;
};

function errorCode(err: unknown): string {
  if (err && typeof err === "object" && "errorCode" in err) {
    return String((err as { errorCode?: string }).errorCode ?? "");
  }
  return "";
}

function isTransientAuthError(err: unknown): boolean {
  return TRANSIENT_ERROR_CODES.has(errorCode(err));
}

function isInteractionRequired(err: unknown): boolean {
  return err instanceof InteractionRequiredAuthError;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function clearInteractiveLoginAttempt(): void {
  sessionStorage.removeItem(INTERACTIVE_AT_KEY);
}

/** De app kan geen token meer halen. De schermwisseling gebeurt in React, zonder harde reload. */
export function notifyAuthRequired(): void {
  window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
}

function currentAccount(): AccountInfo | null {
  return msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
}

/** Zorgt dat twee gelijktijdige aanroepen niet twee keer naar Microsoft sturen. */
let redirectLock = false;

/**
 * Stuurt de browser één keer naar Microsoft.
 * `force` is voor de knop op het aanmeldscherm, zodat die niet door de cooldown wordt tegengehouden.
 */
export async function beginInteractiveLogin(options?: { force?: boolean }): Promise<boolean> {
  if (redirectLock) return true;
  const prev = Number(sessionStorage.getItem(INTERACTIVE_AT_KEY) || 0);
  const coolingDown = prev > 0 && Date.now() - prev < INTERACTIVE_COOLDOWN_MS;
  if (!options?.force && coolingDown) {
    return false;
  }
  redirectLock = true;
  sessionStorage.setItem(INTERACTIVE_AT_KEY, String(Date.now()));
  try {
    const account = currentAccount();
    if (account) {
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    } else {
      await msalInstance.loginRedirect({ ...loginRequest });
    }
    return true;
  } catch {
    redirectLock = false;
    clearInteractiveLoginAttempt();
    return false;
  }
}

/**
 * Haalt een geldig Microsoft-id-token op.
 * Eerst stil (vernieuwen op de achtergrond). Lukt dat niet omdat de sessie
 * na ongeveer een dag verlopen is, dan één keer doorsturen naar Microsoft.
 */
export async function acquireIdToken(options?: {
  forceRefresh?: boolean;
  allowInteractive?: boolean;
}): Promise<IdTokenResult> {
  await msalInitPromise;
  const account = currentAccount();
  if (!account) {
    return { token: null, redirecting: false };
  }
  if (!msalInstance.getActiveAccount()) {
    msalInstance.setActiveAccount(account);
  }

  let lastErr: unknown;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      const res = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
        forceRefresh: Boolean(options?.forceRefresh) && attempt === 0,
      });
      if (res.idToken) {
        clearInteractiveLoginAttempt();
        return { token: res.idToken, redirecting: false };
      }
    } catch (err) {
      lastErr = err;
      if (isInteractionRequired(err)) break;
      if (isTransientAuthError(err) && attempt < 2) {
        await sleep(400 * (attempt + 1));
        continue;
      }
      break;
    }
  }

  if (options?.allowInteractive && isInteractionRequired(lastErr)) {
    const redirecting = await beginInteractiveLogin();
    return { token: null, redirecting };
  }
  return { token: null, redirecting: false };
}
